import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
} from "vue";
import type { ResourceLayout } from "../types/chat";
import { resolveChatLayout } from "../lib/layout";

/**
 * Tracks an element's width via `ResizeObserver` (client-only). Kept local so
 * the shared `src/chat` module needs no `@vueuse/core` bare import (which would
 * resolve from the module's own `node_modules`, not the host app's). On the
 * server the width stays `0`, so the layout falls back to its preference.
 */
function useElementWidth(target: Ref<HTMLElement | null>): Ref<number> {
  const width = ref(0);
  let observer: ResizeObserver | null = null;

  function observe() {
    observer?.disconnect();
    if (!import.meta.client || !target.value) {
      return;
    }
    observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        width.value = entry.contentRect.width;
      }
    });
    observer.observe(target.value);
    width.value = target.value.clientWidth;
  }

  onMounted(observe);
  watch(target, observe);
  onBeforeUnmount(() => observer?.disconnect());

  return width;
}

/**
 * Resolves how the governing resource is laid out beside the chat takeover.
 *
 * The decision combines a width measurement of the takeover body with the
 * agent's preferred layout and an optional explicit override:
 *
 *  - below `inlineBelow` px the resource is **forced inline** (mobile / narrow);
 *  - otherwise an explicit `override` wins, then the `agentLayout` preference,
 *    finally defaulting to `split`.
 *
 * `forcedInline` is gated on an actual measurement (`isMeasured`) so the first,
 * pre-measure frame keeps the preferred (typically `split`) layout instead of
 * flashing inline while the width is still `0`.
 */
export interface UseChatLayoutOptions {
  /** The governing agent's preferred layout. */
  agentLayout?: MaybeRefOrGetter<ResourceLayout | undefined>;
  /** Whether a resource is actually present to lay out. */
  hasResource?: MaybeRefOrGetter<boolean>;
  /** Explicit caller override; `auto`/undefined defers to agent + width. */
  override?: MaybeRefOrGetter<ResourceLayout | undefined>;
  /** Width (px) below which the resource is forced inline. Defaults to 880. */
  inlineBelow?: number;
}

export type EffectiveLayout = "inline" | "split";

export function useChatLayout(
  target: Ref<HTMLElement | null>,
  options: UseChatLayoutOptions = {},
) {
  const width = useElementWidth(target);
  const inlineBelow = options.inlineBelow ?? 880;

  const decision = computed(() =>
    resolveChatLayout({
      width: width.value,
      inlineBelow,
      override: toValue(options.override),
      agentLayout: toValue(options.agentLayout),
      hasResource: toValue(options.hasResource) ?? false,
    }),
  );

  return {
    layout: computed(() => decision.value.layout),
    showSplit: computed(() => decision.value.showSplit),
    showInline: computed(() => decision.value.showInline),
    width,
    isMeasured: computed(() => decision.value.isMeasured),
    forcedInline: computed(() => decision.value.forcedInline),
  };
}
